import React, { useState } from "react";
import { Flex, Spinner, Text, Textarea } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";

interface ReopenMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isSubmitting: boolean;
}

export const ReopenMeetingModal: React.FC<ReopenMeetingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const [reason, setReason] = useState("");

  const handleClose = () => {
    if (!isSubmitting) {
      setReason("");
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm(reason);
  };

  const canProceed = reason.trim().length >= 10;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reopen Panel Meeting"
      size="md"
      modalType="alert"
      iconType="warning"
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      customFooter={
        <Flex gap={3} justify="flex-end" w="100%">
          <Button
            btntype="secondary"
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            btntype="primary"
            type="button"
            onClick={handleConfirm}
            disabled={!canProceed || isSubmitting}
            data-testid="reopen-confirm-btn"
          >
            {isSubmitting ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                Reopening...
              </Flex>
            ) : (
              "Reopen Meeting"
            )}
          </Button>
        </Flex>
      }
    >
      <div className="space-y-4">
        <Text fontSize="sm" color="gray.700">
          Reopening the meeting will set it back to &ldquo;In Progress&rdquo; so
          you can revise consensus scores. Existing scores will be preserved.
        </Text>

        <div>
          <Text fontSize="sm" fontWeight="medium" mb={1} as="label" htmlFor="reopen-reason">
            Reason for reopening
          </Text>
          <Textarea
            id="reopen-reason"
            placeholder="Enter a reason (minimum 10 characters)"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            isDisabled={isSubmitting}
            data-testid="reopen-reason-input"
          />
          {reason.length > 0 && reason.trim().length < 10 && (
            <Text fontSize="xs" color="red.500" mt={1}>
              Reason must be at least 10 characters.
            </Text>
          )}
        </div>
      </div>
    </Modal>
  );
};
